import { ErrorLink } from "@apollo/client/link/error";
import { catchError, from, switchMap, tap } from "rxjs";
import { APP_CHECK_FAILURE, reportAppCheckFailure, reportShapeRefusal } from "../errorReporting";
import { classifySessionRefusal, type ShapeRefusalCode } from "./sessionRefusal";
import {
  credentialFromContext,
  raiseSessionEnd,
  type SessionCredential,
  type SessionEndSignal,
} from "./sessionEnd";
import type { ForcedIdTokenRecovery } from "./tokenRecovery";
import { appCheckFailure, recordAppCheckAttempt } from "../lib/appCheckRecovery";

/**
 * THE SESSION BOUNDARY ON EVERY GRAPHQL CLIENT: App's /core, Book's /app and
 * Book's /core. One classifier (sessionRefusal.ts), one set of rules:
 *
 *   session-revoked          credentialed: raise the session-end signal. No
 *                            refresh, no retry (a refreshed token keeps the
 *                            old auth_time and would be refused again).
 *   recent-sign-in-required  nothing here: the caller decides. Never a sign-out.
 *   refresh-token            credentialed: force-refresh ONCE through the SDK
 *                            (no reason, or a reason this build does not
 *                            know). SDK says the identity is invalid: raise
 *                            the signal. Refreshed: a QUERY is re-issued once
 *                            with the fresh token; a MUTATION is never
 *                            replayed automatically (Backend, final shapes
 *                            2026-10-06), so its error reaches the caller.
 *   retry-preserving-session nothing: the session stays. Any retry is the
 *                            person's (a button), no backoff; the one
 *                            exception is Book's single capped reload of a
 *                            refused FIRST read (appCheckRecovery.ts).
 *   leave-surface            nothing: the screen leaves.
 *   app-check-rejected       Backend refused the APP (APP_CHECK_REJECTED,
 *                            403, before identity), for any request,
 *                            credentialed or not. The session stays: no
 *                            signal, no token refresh, no replay. The
 *                            failure is RECORDED as App Check's `refused`
 *                            (shared/lib/appCheckRecovery.ts), so the
 *                            screen's Try again (retryApiRead) reloads: the
 *                            SDK's throttle lives in page memory and an
 *                            in-page retry is refused again. Reported only
 *                            through the existing once-per-page App Check
 *                            budget, as the fixed kind `refused`.
 *   shape-refused            one fixed, classified report; never retried.
 *
 * Anonymous and token-less requests are never refreshed and never raise the
 * signal (the anonymous rule): a Book guest's UID is their booking's
 * clientId, and losing it is unrecoverable.
 *
 * This link never signs out. It raises; one owner acts (sessionEnd.ts).
 */
export interface SessionLinkOptions {
  /** Refresh the request's own user, `uid`; anyone else answers `unavailable` (tokenRecovery.ts). */
  forceRefreshIdToken(uid: string): Promise<ForcedIdTokenRecovery>;
  /** Defaults to the shared signal. Injected by tests. */
  endSession?(signal: SessionEndSignal): void;
  /**
   * Whether this surface sits behind the token-checking gateway (Backend,
   * rc.3): true for /api/v1/core (the default), false for /api/v1/app, where
   * a gateway-shaped 401 never comes and must not start a refresh.
   */
  gateway?: boolean;
  /** Defaults to shared error reporting. Injected by tests. */
  reportShapeRefusal?(code: ShapeRefusalCode): void;
  /** Defaults to recording `refused` and the fixed App Check report. Injected by tests. */
  appCheckRejected?(): void;
}

/** Backend refused the app's App Check token: Try again must reload. */
function recordAppCheckRejected(): void {
  // Keep a more specific client-side record: a reCAPTCHA that never answered
  // (`unreachable`) is why Backend then refused, and its approved copy names
  // the ad blocker. Only record `refused` when nothing explains it already.
  if (appCheckFailure() === null) recordAppCheckAttempt("refused");
  reportAppCheckFailure(APP_CHECK_FAILURE.refused);
}

/** Context set on the one replay, so a second failure never starts another cycle. */
export const REFRESH_RETRIED_CONTEXT_KEY = "retriedAfterTokenRefresh";
/** The token the replay must carry; each app's auth link prefers it. */
export const FORCED_ID_TOKEN_CONTEXT_KEY = "forcedIdToken";

/** The status Apollo's HttpLink left on the context (the graphql-response+json path). */
function contextStatus(context: Record<string, unknown>): number | null {
  const response = context.response as { status?: unknown } | undefined;
  return typeof response?.status === "number" ? response.status : null;
}

export function createSessionLink(options: SessionLinkOptions): ErrorLink {
  const endSession = options.endSession ?? raiseSessionEnd;
  const report = options.reportShapeRefusal ?? reportShapeRefusal;
  const appCheckRejected = options.appCheckRejected ?? recordAppCheckRejected;

  /** What a refusal on the one replay may still do. Never another refresh. */
  function afterReplay(error: unknown, uid: string, status: number | null): void {
    const refusal = classifySessionRefusal(error, { status });
    if (refusal?.kind === "session-revoked") endSession({ reason: "session-revoked", uid });
    else if (refusal?.kind === "shape-refused") report(refusal.code);
    else if (refusal?.kind === "app-check-rejected") appCheckRejected();
    // A second refresh-token answer after a successful SDK refresh is not
    // proof the identity is gone (it can be App Check or a transient context
    // failure). The session stays; the screen's one user-driven retry follows.
  }

  return new ErrorLink(({ error, operation, forward }) => {
    const context = operation.getContext() as Record<string, unknown>;
    const refusal = classifySessionRefusal(error, { status: contextStatus(context) });
    if (!refusal) return;
    // The gateway shape comes from /api/v1/core only (Backend, rc.3): on a
    // surface without the gateway it is not a session answer at all, and
    // the error reaches the caller untouched.
    if (refusal.kind === "refresh-token" && refusal.source === "gateway" && options.gateway === false) return;
    const credential: SessionCredential = credentialFromContext(context);

    switch (refusal.kind) {
      case "shape-refused":
        report(refusal.code);
        return;
      case "app-check-rejected":
        // Whoever sent it: the refusal is about the app, not the person.
        appCheckRejected();
        return;
      case "session-revoked":
        if (credential.kind === "credentialed") endSession({ reason: "session-revoked", uid: credential.uid });
        return;
      case "recent-sign-in-required":
      case "retry-preserving-session":
      case "leave-surface":
        return;
      case "refresh-token":
        break;
    }

    if (credential.kind !== "credentialed") return;
    const { uid } = credential;
    // Re-entry guard, DEFENCE IN DEPTH: in Apollo 4 the replay's own failure
    // is delivered to the catchError/tap below, not back into this handler,
    // so this is not what stops a loop today. It stays so that a future link
    // order or Apollo change cannot turn one refusal into a refresh cycle.
    if (context[REFRESH_RETRIED_CONTEXT_KEY]) return;

    return from(options.forceRefreshIdToken(uid)).pipe(
      switchMap((refreshed) => {
        if (refreshed.kind === "reauthenticate") {
          endSession({ reason: "identity-invalid", uid });
          throw error;
        }
        if (refreshed.kind === "unavailable") throw error;
        // NEVER an automatic mutation replay, on either surface: a write is
        // resent only by a person (Book's Check under the same key).
        if (operation.operationType === "mutation") throw error;

        operation.setContext((previous: Record<string, unknown>) => ({
          ...previous,
          [REFRESH_RETRIED_CONTEXT_KEY]: true,
          [FORCED_ID_TOKEN_CONTEXT_KEY]: refreshed.token,
        }));
        return forward(operation).pipe(
          // A refusal sent as application/graphql-response+json arrives as a
          // RESULT carrying errors, not as a thrown ServerError.
          tap((result) => {
            if (Array.isArray(result.errors) && result.errors.length > 0) {
              afterReplay(result, uid, contextStatus(operation.getContext() as Record<string, unknown>));
            }
          }),
          catchError((retryError: unknown) => {
            afterReplay(retryError, uid, contextStatus(operation.getContext() as Record<string, unknown>));
            throw retryError;
          })
        );
      })
    );
  });
}
